/**
 * <cart-recommendations> — "You may also like" strip for the cart drawer.
 *
 * Always keyed off the LAST item in the cart (whatever was most recently
 * added), matching Shopify's own product-recommendations endpoint (the same
 * one blocks/torras-bundle-ladder.liquid uses on the PDP). Refetches whenever
 * a cart:update event reports a different last product — including right
 * after this component's own quick-add, so the strip keeps cascading to the
 * newest item.
 *
 * The cards themselves are rendered by sections/cart-recommendations.liquid,
 * fetched here as HTML (`?section_id=…`) rather than as JSON. That section
 * needs Liquid because a screen/lens protector's "models" are sibling PRODUCTS
 * behind the `custom.variation_products` metafield, which the JSON endpoint
 * does not expose. This file only fetches, swaps in the markup, and wires the
 * picker + quick-add — plus, since the 2026-09-04 redesign:
 *
 *   • the strip navigator: prev/next arrows in the heading row and a dot
 *     pagination pill under the scroll-snap row (nearest-card sync on scroll,
 *     same pattern as sections/torras-scenes.liquid);
 *   • model pre-match: a protector card's model <select> is switched to the
 *     anchor product's device (from the snapshot's `device`, or the cart
 *     item's Device/Model option) so the shopper is offered the lens
 *     protector for THEIR phone, not the sibling list's first entry.
 */
(function () {
  if (customElements.get('cart-recommendations')) return;

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      this._panel = this.querySelector('[data-t-recs-panel]');
      this._arrows = this.querySelector('[data-t-recs-arrows]');
      this._prev = this.querySelector('[data-t-recs-prev]');
      this._next = this.querySelector('[data-t-recs-next]');
      this._dots = this.querySelector('[data-t-recs-dots]');
      this._cache = {};
      this._lastProductId = this.dataset.productId || '';
      this._snapshot = this._readSnapshot();
      this._anchorDevice = this._deviceFor(this._lastProductId);
      this._navIndex = 0;
      // Top-up sources (cart-you-may-like.liquid, only when cart_recs_backfill is on):
      // { normalized phone name: its collection url } plus an any-phone collection.
      this._backfillDevices = {};
      try {
        var rawDevices = JSON.parse(this.dataset.backfillDevices || '{}');
        for (var dk in rawDevices) this._backfillDevices[this._normalize(dk)] = rawDevices[dk];
      } catch (e) { /* malformed map: no per-phone top-up */ }
      this._backfillUrl = this.dataset.backfillUrl || '';
      this._backfillUrl2 = this.dataset.backfillUrlSecond || '';
      this._backfillCache = {};
      this._renderToken = 0;
      this._reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      // Order we've seen products added in, most recent last — used to
      // re-anchor to the next-most-recent survivor if the current anchor
      // product itself gets removed from the cart.
      this._history = this._lastProductId ? [this._lastProductId] : [];

      try {
        this._money = new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });
      } catch (e) {
        this._money = { format: function (v) { return '$' + v.toFixed(2); } };
      }

      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._onPanelClick = this._onPanelClick.bind(this);
      this._onPanelChange = this._onPanelChange.bind(this);
      this._onPanelScroll = this._onPanelScroll.bind(this);
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onDotsClick = this._onDotsClick.bind(this);
      document.addEventListener('cart:update', this._onCartUpdate);
      if (this._panel) {
        this._panel.addEventListener('click', this._onPanelClick);
        this._panel.addEventListener('change', this._onPanelChange);
        this._panel.addEventListener('scroll', this._onPanelScroll, { passive: true });
      }
      if (this._prev) this._prev.addEventListener('click', this._onPrev);
      if (this._next) this._next.addEventListener('click', this._onNext);
      if (this._dots) this._dots.addEventListener('click', this._onDotsClick);

      if (this._lastProductId) {
        this._load(this._lastProductId);
      } else {
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this._onCartUpdate);
      if (this._panel) {
        this._panel.removeEventListener('click', this._onPanelClick);
        this._panel.removeEventListener('change', this._onPanelChange);
        this._panel.removeEventListener('scroll', this._onPanelScroll);
      }
      if (this._prev) this._prev.removeEventListener('click', this._onPrev);
      if (this._next) this._next.removeEventListener('click', this._onNext);
      if (this._dots) this._dots.removeEventListener('click', this._onDotsClick);
    }

    _money2(cents) {
      return this._money.format((cents || 0) / 100);
    }

    /**
     * Reads the server-rendered { [lineKey]: { qty, productId, device } }
     * snapshot embedded at render time, used as the baseline for the first
     * diff (and as the first source of the anchor's device label).
     */
    _readSnapshot() {
      var script = this.querySelector('[data-t-recs-snapshot]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent) || {};
      } catch (e) {
        return {};
      }
    }

    /**
     * Device label of a cart line, from the cart.js item shape: a case names
     * the shopper's device in its Device/Model option; a protector (one product
     * per model, single "Default Title" variant) only names it in the product
     * title ("… for iPhone 18 Pro (2-Pack)"). Empty string when neither works.
     */
    _deviceFromItem(item) {
      var opts = Array.isArray(item.options_with_values) ? item.options_with_values : [];
      for (var i = 0; i < opts.length; i++) {
        var name = String(opts[i].name || '').toLowerCase();
        if ((name === 'device' || name === 'model') && opts[i].value) return String(opts[i].value);
      }
      return this._deviceFromTitle(item.product_title);
    }

    /**
     * Is this cart line ineligible to be the recommendation anchor?
     *
     * Mirrors snippets/torras-hidden-product.liquid for the client side: the
     * tokens come from data-anchor-skip (rendered by cart-you-may-like.liquid)
     * and are matched against the cart.js item's vendor and product_type.
     * Today that catches Seel's shipping-protection SKU, which becomes the
     * newest line the moment a shopper ticks "Worry-Free Delivery" and would
     * otherwise take over as the anchor — it has no complementary list, so the
     * strip would empty out right at checkout time.
     */
    _skipAsAnchor(item) {
      var tokens = String(this.dataset.anchorSkip || '')
        .toLowerCase().split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      if (!tokens.length || !item) return false;
      var haystack = (String(item.vendor || '') + ' ' + String(item.product_type || '')).toLowerCase();
      for (var i = 0; i < tokens.length; i++) {
        if (haystack.indexOf(tokens[i]) !== -1) return true;
      }
      return false;
    }

    /** "… for iPhone 18 Pro (2-Pack)" → "iPhone 18 Pro"; '' when the title has no "for …". */
    _deviceFromTitle(title) {
      var m = /\bfor\s+([^()]+?)\s*(?:\(|$)/i.exec(String(title || ''));
      return m ? m[1].trim() : '';
    }

    /**
     * Device label recorded in the current snapshot for a product, or ''.
     * The server snapshot carries the metafield/option label in `device` and
     * the product title in `title`; cart.js-derived entries carry `device` only
     * (already resolved through _deviceFromItem).
     */
    _deviceFor(productId) {
      var snap = this._snapshot || {};
      var keys = Object.keys(snap);
      for (var i = 0; i < keys.length; i++) {
        var line = snap[keys[i]];
        if (line && String(line.productId) === String(productId)) {
          var own = String(line.device || '') || this._deviceFromTitle(line.title);
          if (own) return own;
          break;
        }
      }
      return this._cartDevice();
    }

    /**
     * Fallback when the anchor names no phone — a power bank, charger, cable or
     * lanyard is often the newest line (PostHog 2026-10-05: VegSkin 17 Pro + an
     * EasyGo power bank in the cart, and the SunView card stayed on its 18 Pro Max
     * default). Use the cart's phone instead, but ONLY when every line that names
     * one names the same one: a cart holding a 17 Pro case and an 18 Pro Max glass
     * is ambiguous, and a wrong guess is worse than the card's own default.
     */
    _cartDevice() {
      var snap = this._snapshot || {};
      var keys = Object.keys(snap);
      var found = '';
      for (var i = 0; i < keys.length; i++) {
        var line = snap[keys[i]];
        if (!line || line.skip) continue;
        var device = String(line.device || '') || this._deviceFromTitle(line.title);
        if (!device) continue;
        if (!found) {
          found = device;
        } else if (this._normalize(found) !== this._normalize(device)) {
          return '';
        }
      }
      return found;
    }

    /**
     * This store's cart does not preserve line-item insertion order (an
     * automatic order-level discount reorders lines), so `cart.items.last`
     * cannot tell you what was just added. Instead, diff quantities per line
     * key against the previous snapshot: whichever line is new or grew is
     * the product that was just added — regardless of where it now sits in
     * the array.
     */
    _onCartUpdate(event) {
      var cart = event.detail && event.detail.resource;
      if (!cart || !Array.isArray(cart.items)) return;

      if (!cart.items.length) {
        this._snapshot = {};
        this.hidden = true;
        return;
      }

      var previous = this._snapshot;
      var next = {};
      var addedProductId = null;
      var bestDelta = 0;

      var self = this;
      var eligibleProductIds = {};
      cart.items.forEach(function (item) {
        var skip = self._skipAsAnchor(item);
        next[item.key] = {
          qty: item.quantity,
          productId: item.product_id,
          device: self._deviceFromItem(item),
          type: String(item.product_type || '').toLowerCase().trim(),
          skip: skip,
        };
        if (skip) return;
        eligibleProductIds[item.product_id] = true;
        var prevQty = previous[item.key] ? previous[item.key].qty : 0;
        var delta = item.quantity - prevQty;
        if (delta > bestDelta) {
          bestDelta = delta;
          addedProductId = item.product_id;
        }
      });

      this._snapshot = next;

      // "Still in the cart" (keep the current anchor) vs "may BECOME the
      // anchor" are different questions — a skipped line counts for neither,
      // so both maps below are built from eligible lines only.
      var survivingProductIds = eligibleProductIds;

      if (addedProductId != null) {
        var addedId = String(addedProductId);
        this._remember(addedId);
        if (addedId !== this._lastProductId) {
          this._setAnchor(addedId);
        } else if (this._panel && this._panel.childElementCount) {
          this.hidden = false;
        }
        return;
      }

      // Nothing grew (a removal, or an unrelated update). If the current
      // anchor product is still in the cart, leave it alone. If it was the
      // one just removed, fall back to the next-most-recently-added product
      // that's still present — otherwise "you may also like" would keep
      // showing recommendations for a product no longer in the cart.
      if (this._lastProductId && survivingProductIds[this._lastProductId]) {
        if (this._panel && this._panel.childElementCount) this.hidden = false;
        return;
      }

      for (var i = this._history.length - 1; i >= 0; i--) {
        var candidate = this._history[i];
        if (survivingProductIds[candidate]) {
          this._setAnchor(candidate);
          return;
        }
      }

      // No history match (e.g. a pre-existing line we never saw "added") —
      // fall back to any eligible product still in the cart. A cart holding
      // nothing but skipped lines has no anchor at all: hide rather than
      // recommend off a service SKU.
      var keys = Object.keys(next);
      for (var k = 0; k < keys.length; k++) {
        if (!next[keys[k]].skip) {
          this._setAnchor(String(next[keys[k]].productId));
          return;
        }
      }
      this.hidden = true;
    }

    _remember(productId) {
      if (this._history[this._history.length - 1] === productId) return;
      this._history.push(productId);
      if (this._history.length > 20) this._history.shift();
    }

    _setAnchor(productId) {
      this._lastProductId = productId;
      this.dataset.productId = productId;
      this._anchorDevice = this._deviceFor(productId);
      this._load(productId);
    }

    /**
     * Fetches + caches recommendations for productId WITHOUT touching the
     * visible panel. Exposed separately from _load so a quick-add can warm
     * the cache for its likely-next anchor while the add-to-cart request is
     * still in flight — by the time the resulting cart:update asks to render
     * that anchor, the data is often already there instead of triggering a
     * fresh (visibly delayed) fetch.
     */
    _fetchRecs(productId) {
      var self = this;
      if (this._cache[productId] != null) return Promise.resolve(this._cache[productId]);

      var url =
        this.dataset.url +
        '?section_id=' + encodeURIComponent(this.dataset.sectionId || 'cart-recommendations') +
        '&product_id=' + encodeURIComponent(productId) +
        '&limit=' + encodeURIComponent(this.dataset.limit || '4') +
        '&intent=' + encodeURIComponent(this.dataset.intent || 'related');

      return fetch(url)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          // Section renders arrive wrapped in Shopify's own <div id="shopify-section-…">.
          // Only the cards inside [data-t-recs-cards] belong in the flex strip — that
          // wrapper would become a single flex child and collapse the whole layout.
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelector('[data-t-recs-cards]');
          var markup = cards ? cards.innerHTML.trim() : '';
          self._cache[productId] = markup;
          return markup;
        })
        .catch(function () {
          return '';
        });
    }

    /**
     * Fetches every card source for the anchor in parallel — its curated list, then
     * (Theme settings → cart_recs_backfill) its phone's collection and the any-phone
     * collection (Charging) — and renders once all are in, so the strip never
     * reorders under the shopper. A newer anchor wins over a slower response.
     */
    _load(productId) {
      var self = this;
      var token = ++this._renderToken;
      var sources = [this._fetchRecs(productId)];
      // A top-up source that hangs must not hold the strip hostage: after 4 s it
      // counts as empty for this render (its response is still cached for the next).
      this._topUpUrls().forEach(function (url) {
        sources.push(Promise.race([
          self._fetchCollectionCards(url),
          new Promise(function (resolve) { setTimeout(function () { resolve(''); }, 4000); }),
        ]));
      });
      Promise.all(sources).then(function (markups) {
        if (token !== self._renderToken) return;
        self._render(markups);
      });
    }

    /** The top-up collection urls for the current anchor's phone, most specific first. */
    _topUpUrls() {
      var urls = [];
      var want = this._normalize(this._anchorDevice);
      var deviceUrl = want ? this._backfillDevices[want] : '';
      if (deviceUrl) urls.push(deviceUrl);
      if (this._backfillUrl && this._backfillUrl !== deviceUrl) urls.push(this._backfillUrl);
      if (this._backfillUrl2 && urls.indexOf(this._backfillUrl2) < 0) urls.push(this._backfillUrl2);
      return urls;
    }

    _render(markups) {
      if (!this._panel) return;

      var panel = this._panel;
      panel.innerHTML = '';
      this._pickCards(markups).forEach(function (card) {
        panel.appendChild(card);
      });
      this.hidden = panel.childElementCount === 0;
      panel.scrollLeft = 0;
      this._prematchModels();
      this._buildNav();
    }

    /**
     * ONE PER TYPE FIRST (2026-10-08). The sources are pooled in order — curated
     * list, phone collection, Charging — minus products already in the cart and
     * cards that can't be set to the cart's phone. Then up to Max products are
     * picked in three passes over that pool:
     *   1. the first card of each product type the cart doesn't hold yet;
     *   2. the first card of each type it does hold;
     *   3. whatever is left, in pool order.
     * Before this, the curated list led and the collections only topped it up, so
     * an iPhone 16 Pro Max protector cart got three power banks and two chargers
     * while the phone's in-stock case sat one slot past the cut.
     */
    _pickCards(markups) {
      var self = this;
      var limit = Math.max(2, Number(this.dataset.limit || 4));
      var want = this._normalize(this._anchorDevice);

      var exclude = {};
      exclude[String(this._lastProductId)] = true;
      var cartTypes = {};
      var snap = this._snapshot || {};
      Object.keys(snap).forEach(function (k) {
        if (!snap[k]) return;
        exclude[String(snap[k].productId)] = true;
        if (snap[k].type && !snap[k].skip) cartTypes[snap[k].type] = true;
      });

      var pool = [];
      var seen = {};
      var holder = document.createElement('div');
      markups.forEach(function (markup) {
        holder.innerHTML = markup || '';
        Array.prototype.slice.call(holder.children).forEach(function (card) {
          var id = String(card.getAttribute('data-t-recs-product-id') || '');
          if (!id || exclude[id] || seen[id]) return;
          if (want && !self._fits(card, want)) return;
          // A protector card is one product of a sibling family, and _prematchModels
          // switches it to the cart's phone — which may be the very product already in
          // the cart (CameraGo 18 Pro card → 18 Pro Max, with 18 Pro Max in the cart).
          var target = self._prematchTargetId(card, want);
          if (target && (exclude[target] || seen[target])) return;
          if (target) seen[target] = true;
          seen[id] = true;
          pool.push(card);
        });
      });

      var picked = [];
      var used = [];
      var shownTypes = {};
      var pass = function (accept) {
        pool.forEach(function (card, i) {
          if (picked.length >= limit || used[i]) return;
          var type = card.getAttribute('data-t-recs-type') || '';
          if (!accept(type)) return;
          used[i] = true;
          shownTypes[type] = true;
          picked.push(card);
        });
      };
      pass(function (type) { return !shownTypes[type] && !cartTypes[type]; });
      pass(function (type) { return !shownTypes[type]; });
      pass(function () { return true; });
      return picked;
    }

    /* ── Strip navigator ──────────────────────────────────────────────── */

    _cards() {
      return this._panel ? Array.prototype.slice.call(this._panel.querySelectorAll('.t-cartx-recs__card')) : [];
    }

    /** Left inset = the strip's padding (= scroll-padding), so a snapped card's scrollLeft ≈ offsetLeft − inset. */
    _inset() {
      return this._panel ? parseFloat(getComputedStyle(this._panel).paddingLeft) || 0 : 0;
    }

    _nearest() {
      var cards = this._cards();
      var target = this._panel.scrollLeft + this._inset();
      var best = 0;
      var min = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var d = Math.abs(cards[i].offsetLeft - target);
        if (d < min) {
          min = d;
          best = i;
        }
      }
      return best;
    }

    _buildNav() {
      var n = this._cards().length;
      var show = n > 1;
      if (this._arrows) this._arrows.hidden = !show;
      if (this._dots) {
        this._dots.hidden = !show;
        var html = '';
        for (var i = 0; i < (show ? n : 0); i++) {
          html +=
            '<button type="button" class="t-cartx-recs__dot" data-t-recs-dot="' + i + '" ' +
            'aria-label="Recommendation ' + (i + 1) + ' of ' + n + '"></button>';
        }
        this._dots.innerHTML = html;
      }
      this._syncNav(0);
    }

    _syncNav(index) {
      this._navIndex = index;
      var n = this._cards().length;
      if (this._dots) {
        this._dots.querySelectorAll('.t-cartx-recs__dot').forEach(function (dot, i) {
          var on = i === index;
          dot.classList.toggle('on', on);
          if (on) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
      if (this._prev) this._prev.disabled = index <= 0;
      if (this._next) this._next.disabled = index >= n - 1;
    }

    _goTo(index) {
      var cards = this._cards();
      if (!cards.length) return;
      index = Math.max(0, Math.min(cards.length - 1, index));
      this._panel.scrollTo({
        left: Math.max(0, cards[index].offsetLeft - this._inset()),
        behavior: this._reduceMotion ? 'auto' : 'smooth',
      });
      this._syncNav(index); // optimistic; the scroll handler re-confirms from the real position
    }

    _onPrev() {
      this._goTo(this._navIndex - 1);
    }

    _onNext() {
      this._goTo(this._navIndex + 1);
    }

    _onDotsClick(event) {
      var dot = event.target.closest('[data-t-recs-dot]');
      if (!dot) return;
      this._goTo(parseInt(dot.getAttribute('data-t-recs-dot'), 10));
    }

    _onPanelScroll() {
      var self = this;
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = requestAnimationFrame(function () {
        self._scrollRaf = null;
        self._syncNav(self._nearest());
      });
    }

    /* ── Model pre-match ──────────────────────────────────────────────── */

    /**
     * Switches every protector card's model <select>, and every case card's
     * Device/Model option <select>, to the option whose label equals the
     * anchor's device ("iPhone 18 Pro"), then applies it exactly as a shopper's
     * own change would. No-op when the anchor has no device label or the card
     * doesn't carry that model.
     */
    _prematchModels() {
      var want = this._normalize(this._anchorDevice);
      if (!want || !this._panel) return;
      var self = this;
      this._panel.querySelectorAll('select[data-t-recs-models]').forEach(function (select) {
        var options = Array.prototype.slice.call(select.options);
        var index = -1;
        for (var i = 0; i < options.length; i++) {
          if (self._normalize(options[i].textContent) === want) {
            index = i;
            break;
          }
        }
        if (index < 0 || index === select.selectedIndex) return;
        select.selectedIndex = index;
        var card = select.closest('.t-cartx-recs__card');
        if (card) self._onModelChange(select, card);
      });

      // Cases name the phone in a real Shopify option (Device / Model), rendered as a
      // plain option <select>, not as sibling products — the loop above never saw them,
      // so a 17 Pro cart was offered the Q3 Air on its first variant, 17 Pro Max
      // (reported 2026-10-07). Same rule: switch to the anchor's device, keeping the
      // card's other choices (colour). If that combination doesn't exist, leave the
      // card alone rather than show a selection the Add button can't honour.
      this._panel.querySelectorAll('select[data-t-recs-opt]').forEach(function (select) {
        var name = self._normalize(select.getAttribute('aria-label'));
        if (name !== 'device' && name !== 'model') return;
        var options = Array.prototype.slice.call(select.options);
        var index = -1;
        for (var i = 0; i < options.length; i++) {
          if (self._normalize(options[i].textContent) === want) {
            index = i;
            break;
          }
        }
        if (index < 0 || index === select.selectedIndex) return;
        var card = select.closest('.t-cartx-recs__card');
        if (!card) return;
        var optIndex = Number(select.dataset.optIndex);
        var target = self._inStockFor(card, optIndex, options[index].value);
        if (!target) return;
        // Carry the target's OTHER choices (usually colour) into the card first, so
        // _onOptionSelect lands on an in-stock variant, then sync the visible controls.
        card.dataset.tRecsOpts = JSON.stringify(target.opts);
        self._syncOptionControls(card, target.opts);
        self._onOptionSelect(card, optIndex, options[index].value);
      });
    }

    /**
     * Phones a card can be set to: its protector model picker, its case
     * Device/Model option, or (no picker) the single phone the server stamped on
     * it. Empty for phone-agnostic products — chargers, cables, lanyards.
     */
    _cardDevices(card) {
      var self = this;
      var labels = [];
      card.querySelectorAll('select[data-t-recs-models], select[data-t-recs-opt]').forEach(function (select) {
        if (select.hasAttribute('data-t-recs-opt')) {
          var name = self._normalize(select.getAttribute('aria-label'));
          if (name !== 'device' && name !== 'model') return;
          // A case option <select> lists every Device value, sold out or not; only
          // phones with an in-stock variant count (2026-10-07 per-device test: Q3 Air
          // 17 Pro Max lists iPhone 15 Pro…17, all sold out).
          var index = Number(select.dataset.optIndex);
          Array.prototype.forEach.call(select.options, function (option) {
            if (self._inStockFor(card, index, option.value)) labels.push(option.textContent);
          });
          return;
        }
        // Protector model pickers only render in-stock siblings already.
        Array.prototype.forEach.call(select.options, function (option) {
          labels.push(option.textContent);
        });
      });
      if (!labels.length && card.dataset.tRecsDevice) labels.push(card.dataset.tRecsDevice);
      return labels;
    }

    /**
     * The sibling product a protector card's model picker will be switched to for
     * `want` (same match as _prematchModels), or '' when it has no picker or no match.
     */
    _prematchTargetId(card, want) {
      if (!want) return '';
      var select = card.querySelector('select[data-t-recs-models]');
      if (!select) return '';
      for (var i = 0; i < select.options.length; i++) {
        if (this._normalize(select.options[i].textContent) === want) {
          return String(select.options[i].getAttribute('data-product-id') || '');
        }
      }
      return '';
    }

    /** True when the card is phone-agnostic or can be set to `want` (a normalized phone). */
    _fits(card, want) {
      var self = this;
      var devices = this._cardDevices(card);
      if (!devices.length) return true;
      return devices.some(function (label) { return self._normalize(label) === want; });
    }

    /** Cards for a collection, from the same section rendered on the collection url. */
    _fetchCollectionCards(url) {
      if (this._backfillCache[url] != null) return Promise.resolve(this._backfillCache[url]);
      var self = this;
      var full = url + (url.indexOf('?') < 0 ? '?' : '&') + 'section_id=' + encodeURIComponent(this.dataset.sectionId || 'cart-recommendations');
      return fetch(full)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelector('[data-t-recs-cards]');
          var markup = cards ? cards.innerHTML.trim() : '';
          self._backfillCache[url] = markup;
          return markup;
        })
        .catch(function () {
          return '';
        });
    }

    /** The card's variant map (sections/cart-recommendations.liquid), or []. */
    _cardVariants(card) {
      var script = card.querySelector('[data-t-recs-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    /**
     * An IN-STOCK variant with option `index` = `value`: the one that keeps the
     * card's other choices (colour) when that is in stock, else the first in-stock
     * one for that value. null when every variant for it is sold out — the card
     * must never be switched onto something its Add button can't add.
     */
    _inStockFor(card, index, value) {
      var variants = this._cardVariants(card);
      var current;
      try {
        current = JSON.parse(card.dataset.tRecsOpts || '[]');
      } catch (e) {
        current = [];
      }
      var forValue = variants.filter(function (v) { return v.available && v.opts && v.opts[index] === value; });
      var same = forValue.find(function (v) {
        return v.opts.every(function (o, i) { return i === index || o === current[i]; });
      });
      return same || forValue[0] || null;
    }

    /** Points the card's option selects and colour dots at `opts` (a variant's options). */
    _syncOptionControls(card, opts) {
      card.querySelectorAll('select[data-t-recs-opt]').forEach(function (select) {
        var want = opts[Number(select.dataset.optIndex)];
        for (var i = 0; i < select.options.length; i++) {
          if (select.options[i].value === want) {
            select.selectedIndex = i;
            break;
          }
        }
      });
      card.querySelectorAll('.t-cartx-recs__swatch[data-opt-index]').forEach(function (dot) {
        var on = opts[Number(dot.dataset.optIndex)] === dot.dataset.value;
        dot.setAttribute('aria-pressed', String(on));
        if (on) {
          var row = dot.closest('.t-cartx-recs__swatch-row');
          var nameEl = row ? row.querySelector('[data-t-recs-swatch-name]') : null;
          if (nameEl) nameEl.textContent = dot.dataset.value || '';
        }
      });
    }

    // "Galaxy" is dropped because the catalogue names the same phone both ways:
    // protectors' device_model says "Samsung Galaxy S26 Ultra", case Device
    // options say "Samsung S26 Ultra" (checked 2026-10-07).
    _normalize(value) {
      return String(value || '').toLowerCase().replace(/\bgalaxy\b/g, ' ').replace(/\s+/g, ' ').trim();
    }

    /** Repoints price, image and the quick-add target. Any field left undefined stays as it is. */
    _applyToCard(card, next) {
      var add = card.querySelector('.t-cartx-recs__add');
      if (add) {
        if (next.variantId) add.dataset.variantId = next.variantId;
        // Sibling-product options retarget the quick-add (and the recommendation
        // cache warm-up) at that product; plain variant options leave it as it was.
        if (next.productId) add.dataset.productId = next.productId;
        if (next.available != null) add.disabled = !next.available;
        if (next.title) add.setAttribute('aria-label', 'Add ' + next.title + ' to cart');
      }

      var price = card.querySelector('.t-cartx-recs__price');
      if (price && next.price != null) price.textContent = this._money2(next.price);

      var title = card.querySelector('[data-t-recs-title]');
      if (title && next.title) title.textContent = next.title;

      if (next.url) {
        card.querySelectorAll('[data-t-recs-link]').forEach(function (link) {
          link.href = next.url;
        });
      }

      var image = card.querySelector('[data-t-recs-img]');
      if (image && next.img) {
        image.src = next.img;
        if (next.title) image.alt = next.title;
      }
    }

    /**
     * MODEL picker — a protector's options are sibling PRODUCTS, each carrying
     * everything needed to rewrite the card in place (so picking a model never
     * sends the shopper off to another product page).
     */
    _onModelChange(select, card) {
      var option = select.options[select.selectedIndex];
      if (!option) return;

      this._applyToCard(card, {
        variantId: select.value,
        productId: option.dataset.productId,
        price: parseInt(option.dataset.price, 10),
        title: option.dataset.title,
        url: option.dataset.url,
        img: option.dataset.img,
        available: true,
      });

      if (option.dataset.title) select.setAttribute('aria-label', 'Choose model for ' + option.dataset.title);
    }

    /**
     * Real Shopify options (a case's Device × Colour). Each control owns one
     * option index; the card holds the full value tuple, because single-value
     * options render no control at all yet still take part in the match.
     */
    _onOptionSelect(card, index, value) {
      var script = card.querySelector('[data-t-recs-variants]');
      if (!script) return;

      var variants;
      var selection;
      try {
        variants = JSON.parse(script.textContent);
        selection = JSON.parse(card.dataset.tRecsOpts || '[]');
      } catch (e) {
        return;
      }

      selection[index] = value;
      card.dataset.tRecsOpts = JSON.stringify(selection);

      var match = variants.find(function (variant) {
        return selection.every(function (v, i) { return variant.opts[i] === v; });
      });
      if (!match) return;

      this._applyToCard(card, {
        variantId: match.id,
        price: match.price,
        img: match.img,
        available: match.available,
      });
    }

    _onPanelChange(event) {
      var select = event.target.closest('select.t-cartx-recs__var');
      if (!select) return;

      var card = select.closest('.t-cartx-recs__card');
      if (!card) return;

      if (select.hasAttribute('data-t-recs-models')) {
        this._onModelChange(select, card);
      } else {
        this._onOptionSelect(card, Number(select.dataset.optIndex), select.value);
      }
    }

    _onPanelClick(event) {
      var swatch = event.target.closest('.t-cartx-recs__swatch');
      if (swatch) {
        var swatchCard = swatch.closest('.t-cartx-recs__card');
        if (!swatchCard) return;
        swatch.parentElement.querySelectorAll('.t-cartx-recs__swatch').forEach(function (dot) {
          dot.setAttribute('aria-pressed', String(dot === swatch));
        });
        var row = swatch.closest('.t-cartx-recs__swatch-row');
        var nameEl = row ? row.querySelector('[data-t-recs-swatch-name]') : null;
        if (nameEl) nameEl.textContent = swatch.dataset.value || '';
        this._onOptionSelect(swatchCard, Number(swatch.dataset.optIndex), swatch.dataset.value);
        return;
      }

      var button = event.target.closest('.t-cartx-recs__add');
      if (!button || button.disabled) return;

      // Keep this in-drawer click from bubbling up to the cart drawer dialog's
      // outside-click handler (assets/dialog.js #handleClick → isClickedOutside).
      // A few lines below we swap the button's innerHTML for a spinner, which
      // detaches event.target (the clicked <svg> icon) from the DOM. By the time
      // the same click reaches the dialog, isClickedOutside() sees a target the
      // dialog no longer contains, misreads it as a backdrop click, and closes
      // the drawer — which then immediately re-opens (auto-open) on the add's
      // cart:update, producing a jarring slide-out-then-back-in. A click on our
      // add button is unambiguously inside the drawer, so stop it here.
      event.stopPropagation();

      // `data-variant-id` is the single source of truth: every picker (model
      // select, option select, colour swatch) writes the resolved variant back
      // to the button, because no one control knows the whole combination.
      var variantId = parseInt(button.getAttribute('data-variant-id'), 10);
      if (!variantId) return;

      // The product being added is almost certainly about to become the new
      // anchor once cart:update fires — warm its recommendations cache now,
      // in parallel with the add-to-cart round trip, so the re-render that
      // follows the (unavoidable) server morph reset is instant instead of
      // triggering its own visible fetch delay.
      var productId = button.getAttribute('data-product-id');
      if (productId) this._fetchRecs(productId);

      var originalContent = button.innerHTML;
      button.disabled = true;
      button.classList.add('is-adding');
      button.innerHTML = '<span class="t-cartx-recs__spinner" aria-hidden="true"></span>';

      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset.sectionId && sectionIds.indexOf(el.dataset.sectionId) === -1) {
          sectionIds.push(el.dataset.sectionId);
        }
      });

      var payload = { items: [{ id: variantId, quantity: 1 }] };
      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }

      var cartUrl = this.dataset.cartUrl;

      fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart-recommendations add');
          return r.json();
        })
        .then(function (addResult) {
          return fetch(cartUrl)
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              return { cart: cart, sections: addResult.sections };
            });
        })
        .then(function (result) {
          var ev = new Event('cart:update', { bubbles: true });
          ev.detail = {
            resource: result.cart,
            sourceId: 'cart-recommendations',
            data: {
              source: 'cart-recommendations',
              itemCount: result.cart.item_count,
              sections: result.sections,
            },
          };
          document.dispatchEvent(ev);
        })
        .catch(function () {
          // Swallowed — button state reset happens in `finally` regardless of outcome.
        })
        .finally(function () {
          // On success the panel is normally rebuilt from scratch (new anchor
          // → new cards), which already discards this button. This restore
          // only matters when the button is still around: a failed add, or
          // an add that didn't change the anchor.
          if (button.isConnected) {
            button.disabled = false;
            button.classList.remove('is-adding');
            button.innerHTML = originalContent;
          }
        });
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();
